import Select, { type FilterOptionOption } from "react-select";

type PennDropdownOption = { value: string; label: string };

type PennDropdownProps = {
  options: PennDropdownOption[];
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  error?: boolean;
  isSearchable?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  /** Fired when the menu is scrolled to the bottom — wire to fetch the next page. */
  onMenuScrollToBottom?: () => void;
  /**
   * Fired as the user types (server-side search). When provided, client-side
   * option filtering is disabled — the parent is expected to refetch options.
   */
  onInputChange?: (value: string) => void;
  /**
   * Override react-select's default client-side filter (substring match
   * against `label`). Use this when an option must survive typing that
   * doesn't match its own label — e.g. a fixed "create new" sentinel row
   * that should stay clickable no matter what the caller typed. Ignored
   * when `onInputChange` is set (server-side search already disables
   * client-side filtering).
   */
  filterOption?: (option: FilterOptionOption<PennDropdownOption>, inputValue: string) => boolean;
  name?: string;
  inputId?: string;
  className?: string;
};

export const PennDropdown = ({
  options,
  value,
  onChange,
  onBlur,
  placeholder = "Select…",
  error = false,
  isSearchable = false,
  isDisabled = false,
  isLoading = false,
  onMenuScrollToBottom,
  onInputChange,
  filterOption,
  name,
  inputId,
  className = "",
}: PennDropdownProps) => {
  const selected = options.find((opt) => opt.value === value) ?? null;

  return (
    <Select<PennDropdownOption>
      unstyled
      name={name}
      inputId={inputId}
      options={options}
      value={selected}
      onChange={(opt) => onChange?.(opt?.value ?? "")}
      onBlur={onBlur}
      placeholder={placeholder}
      isSearchable={isSearchable}
      isDisabled={isDisabled}
      isLoading={isLoading}
      onMenuScrollToBottom={onMenuScrollToBottom}
      onInputChange={
        onInputChange
          ? (val, meta) => {
              if (meta.action === "input-change") onInputChange(val);
              return val;
            }
          : undefined
      }
      filterOption={onInputChange ? null : filterOption}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      className={className}
      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
      classNames={{
        control: (state) =>
          [
            "flex items-center justify-between w-full min-h-[42px] px-3 rounded-xl border text-sm bg-gray-50 transition",
            isDisabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
            error
              ? "border-error"
              : state.isFocused
                ? "border-penn-500 ring-2 ring-penn-500"
                : "border-gray-200 hover:border-penn-500",
          ].join(" "),
        valueContainer: () => "flex flex-wrap items-center gap-1",
        placeholder: () => "text-gray-400",
        singleValue: () => "text-penn-text",
        input: () => "text-penn-text",
        indicatorsContainer: () => "flex items-center",
        indicatorSeparator: () => "hidden",
        dropdownIndicator: () => "flex items-center px-1 text-gray-400",
        menu: () =>
          "mt-1 rounded-xl border border-gray-200 bg-white shadow-soft overflow-hidden",
        menuList: () => "py-1 max-h-60 overflow-y-auto",
        option: (state) =>
          [
            "px-3 py-2 text-sm cursor-pointer transition-colors",
            state.isSelected
              ? "bg-penn-500 text-white"
              : state.isFocused
                ? "bg-penn-100 text-penn-text"
                : "text-penn-text",
          ].join(" "),
        noOptionsMessage: () => "px-3 py-2 text-sm text-gray-400",
      }}
    />
  );
};
